import createClient, { type Client, type Middleware } from 'openapi-fetch'
import {
  applyFirebaseAuthHeader,
  applyImpersonationSessionHeaders,
  handleMfaEnrollmentRequiredResponse,
  IMPERSONATION_OPT_OUT_HEADER,
  notifyImpersonationSessionExpired,
} from '~/utils/backendFetch'
export { isNetworkFetchError } from '~/utils/networkError'
export { IMPERSONATION_OPT_OUT_HEADER } from '~/utils/backendFetch'
import type { components, paths } from './openapi'

/**
 * Canonical typed backend transport.
 *
 * Callers must use `getBackendClient()` (or domain modules that do). Do not
 * assemble Firebase bearer tokens or impersonation headers elsewhere.
 * Client construction and middleware registration stay private so each
 * configuration is created once and reused.
 *
 * Auth, impersonation, and MFA policies are implemented in `utils/backendFetch.ts`
 * and shared with the temporary raw-fetch plugin (`plugins/api.client.ts`).
 */
export type BackendClient = Client<paths>
export type ApiSchema<K extends keyof components['schemas']> = components['schemas'][K]

export interface BackendClientOptions {
  auth?: boolean
  credentials?: RequestCredentials
  impersonation?: boolean
}

export const extractApiErrorDetail = (body: unknown): string | null => {
  if (!body || typeof body !== 'object') {
    return null
  }

  const detail = (body as { detail?: unknown }).detail

  if (typeof detail === 'string' && detail.trim()) {
    return detail
  }

  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') {
          return item
        }
        if (item && typeof item === 'object' && typeof (item as { msg?: string }).msg === 'string') {
          return (item as { msg: string }).msg
        }
        return null
      })
      .filter((part): part is string => Boolean(part))

    return parts.length > 0 ? parts.join(' ') : null
  }

  if (detail && typeof detail === 'object') {
    const message = (detail as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) {
      return message
    }
  }

  return null
}

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ApiResponseError) {
    const detail = extractApiErrorDetail(error.error)
    if (detail) {
      return detail
    }
    return error.message || fallback
  }

  if (error instanceof Error && error.message) {
    return error.message
  }

  return fallback
}

export class ApiResponseError extends Error {
  override name = 'ApiResponseError'

  constructor(
    public response: Response,
    public error: unknown,
    message = 'API response returned an error',
  ) {
    const detail = extractApiErrorDetail(error)
    super(detail ?? `${message} (${response.status})`)
  }
}

export const isApiUnauthorizedError = (error: unknown): error is ApiResponseError =>
  error instanceof ApiResponseError && error.response.status === 401

export const isApiForbiddenError = (error: unknown): error is ApiResponseError =>
  error instanceof ApiResponseError && error.response.status === 403

export const isApiNotFoundError = (error: unknown): error is ApiResponseError =>
  error instanceof ApiResponseError && error.response.status === 404

export const isApiTransientError = (error: unknown): error is ApiResponseError =>
  error instanceof ApiResponseError && error.response.status >= 500

/** Gateway/proxy failures (often empty body) that usually succeed on a quick retry. */
export const isApiGatewayError = (error: unknown): error is ApiResponseError =>
  error instanceof ApiResponseError
  && (error.response.status === 502
    || error.response.status === 503
    || error.response.status === 504)

/**
 * Run `operation` once, and if it fails with a gateway-style 5xx, retry once.
 * Safe for idempotent (or effectively idempotent) writes such as call assignment.
 */
export async function withGatewayRetry<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation()
  }
  catch (error) {
    if (!isApiGatewayError(error)) {
      throw error
    }
    return await operation()
  }
}

export class AuthTokenUnavailableError extends Error {
  override name = 'AuthTokenUnavailableError'

  constructor(message = 'Authenticated request skipped because no Firebase token is available') {
    super(message)
  }
}

export const isAuthTokenUnavailableError = (error: unknown): error is AuthTokenUnavailableError =>
  error instanceof AuthTokenUnavailableError

type ApiResult<T> = {
  data?: T
  error?: unknown
  response: Response
}

const resolveBackendBasePath = () => {
  const config = useRuntimeConfig()
  return config.public.BACKEND_URL as string
}

const authMiddleware = (enabled: boolean): Middleware => ({
  async onRequest({ request }) {
    if (!enabled) return

    const applied = await applyFirebaseAuthHeader(request.headers)
    if (!applied) {
      throw new AuthTokenUnavailableError()
    }

    return request
  },
})

const impersonationMiddleware: Middleware = {
  onRequest({ request }) {
    const result = applyImpersonationSessionHeaders(request.headers)
    if (result.expiredCleared) {
      const i18n = useNuxtApp().$i18n as { t: (key: string) => string } | undefined
      notifyImpersonationSessionExpired(
        true,
        i18n?.t('impersonate.sessionExpired'),
      )
    }
    return request
  },
}

const mfaRedirectMiddleware = (backendBasePath: string): Middleware => ({
  async onResponse({ request, response }) {
    const i18n = useNuxtApp().$i18n as { t: (key: string) => string } | undefined
    await handleMfaEnrollmentRequiredResponse(
      request.url,
      response,
      backendBasePath,
      i18n?.t('auth.mfa.enrollmentRequiredRedirect'),
    )
    return response
  },
})

const impersonationOptOutMiddleware: Middleware = {
  onRequest({ request }) {
    request.headers.set(IMPERSONATION_OPT_OUT_HEADER, '1')
    return request
  },
}

const clientCache = new Map<string, BackendClient>()

const backendClientCacheKey = (options: BackendClientOptions): string =>
  [
    options.auth === false ? 'auth:off' : 'auth:on',
    options.impersonation === false ? 'impersonation:off' : 'impersonation:on',
    `credentials:${options.credentials ?? 'default'}`,
  ].join('|')

/** Builds a configured client. Not exported — use `getBackendClient()`. */
const createBackendClient = (
  options: BackendClientOptions = {},
): BackendClient => {
  const backendBasePath = resolveBackendBasePath()
  const client = createClient<paths>({
    baseUrl: backendBasePath,
    credentials: options.credentials,
  })

  client.use(
    authMiddleware(options.auth !== false),
    ...(options.impersonation === false
      ? [impersonationOptOutMiddleware]
      : [impersonationMiddleware]),
    mfaRedirectMiddleware(backendBasePath),
  )

  return client
}

/**
 * Returns the canonical configured backend client for the given options.
 * Instances are cached per options shape so middleware is registered once.
 */
export const getBackendClient = (
  options: BackendClientOptions = {},
): BackendClient => {
  const key = backendClientCacheKey(options)
  const cached = clientCache.get(key)
  if (cached) {
    return cached
  }

  const client = createBackendClient(options)
  clientCache.set(key, client)
  return client
}

/**
 * Always builds a new client without using the cache.
 * Intended for unit tests that inspect middleware registration; app code
 * should call `getBackendClient()` instead.
 */
export const createFreshBackendClientForTests = (
  options: BackendClientOptions = {},
): BackendClient => createBackendClient(options)

/** Clears the client cache. Exported for tests that share a module instance. */
export const resetBackendClientCacheForTests = (): void => {
  clientCache.clear()
}

export const unwrapApiResponse = async <T>(
  responsePromise: Promise<ApiResult<T>>,
): Promise<T> => {
  const result = await responsePromise
  if (result.error || !result.response.ok) {
    throw new ApiResponseError(result.response, result.error)
  }
  return result.data as T
}
